import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import classnames from 'classnames';

import * as api from '../../../api';

import { ModalContainer } from '../../ModalContainer';
import DateTimeFilters from '../../DateRangePicker';
import { ReactComponent as CalendarSvg } from '../../../assets/calendar.svg';
import { ReactComponent as ClockSvg } from '../../../assets/clock.svg';
import { ChevronDown, Icon, Close } from '../../Icon';
import { DateRangeFilters } from '../../DateRangePicker/common';

import styles from '../index.module.css';
import { SchedulingStateProps } from '../utils';

const sortingMap: Record<api.EhrTimeSlot.Sorting, string> = {
  [api.EhrTimeSlot.Sorting.AlphabeticallyPractice]: 'Alphabetically',
  [api.EhrTimeSlot.Sorting.AlphabeticallyProvider]: 'Alphabetically',
  [api.EhrTimeSlot.Sorting.AvailabilityPractice]: 'Availability',
  [api.EhrTimeSlot.Sorting.AvailabilityProvider]: 'Availability',
  [api.EhrTimeSlot.Sorting.Distance]: 'Distance',
};

const displayBySortMap: Record<
  api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy,
  api.EhrTimeSlot.Sorting[]
> = {
  [api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy.Practice]: [
    api.EhrTimeSlot.Sorting.AlphabeticallyPractice,
    api.EhrTimeSlot.Sorting.AvailabilityPractice,
    api.EhrTimeSlot.Sorting.Distance,
  ],
  [api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy.Provider]: [
    api.EhrTimeSlot.Sorting.AlphabeticallyProvider,
    api.EhrTimeSlot.Sorting.AvailabilityProvider,
  ],
};

export interface AppointmentFilters extends DateRangeFilters {
  sort: api.EhrTimeSlot.Sorting;
  providerIds?: string[];
  practiceIds?: string[];
  appointmentKinds?: api.EhrTimeSlot.AppointmentKind[];
}

interface Props extends SchedulingStateProps {
  onFiltersChange: (filters: AppointmentFilters) => void;
  filters: AppointmentFilters;
  displayBy?: api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy;
  daysAllowedFrom: number;
  daysAllowedTo?: number;
  showTimeRange?: boolean;
}

const DATE_FILTER_PILL_FORMAT = 'MMM DD';
const TIME_FILTER_PILL_FORMAT = 'h:mm A';

const Filters: React.FC<Props> = ({
  onFiltersChange,
  filters,
  displayBy,
  daysAllowedFrom,
  daysAllowedTo,
  showTimeRange = true,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { startDay, startTime, endDay, endTime, sort } = filters;
  const onSortChange = async (newSort: api.EhrTimeSlot.Sorting) => {
    return onFiltersChange({
      ...filters,
      sort: newSort,
    });
  };

  const getFiltersText = (): string => {
    if (startDay) {
      let filtersText = `${moment(startDay).format(DATE_FILTER_PILL_FORMAT)}`;
      if (endDay && !moment(startDay).isSame(endDay, 'day')) {
        filtersText += ` - ${moment(endDay).format(
          moment(startDay).isSame(endDay, 'month')
            ? 'DD'
            : DATE_FILTER_PILL_FORMAT,
        )}`;
      }
      return filtersText;
    }
    return t('Search by date');
  };

  return (
    <div className={styles.FiltersContainer}>
      <ModalContainer
        isOpen={isModalOpen}
        noPadding={true}
        onModalClose={() => {
          setIsModalOpen(false);
        }}
        component={
          <DateTimeFilters
            startDay={startDay}
            endDay={endDay}
            startTime={startTime}
            endTime={endTime}
            showTimeRange={showTimeRange}
            onFiltersChange={(dateTimeFilters) => {
              onFiltersChange({ ...filters, ...dateTimeFilters });
              setIsModalOpen(false);
            }}
            daysAllowedFrom={daysAllowedFrom}
            daysAllowedTo={daysAllowedTo}
          />
        }
      />
      <ul className={styles.Filters}>
        <li
          className={classnames({
            [styles.FilterStart]: true,
            [styles.Applied]: !!startDay || !!endDay,
          })}
          onClick={() => setIsModalOpen(true)}
        >
          <CalendarSvg viewBox="0 0 28 24" className={styles.FilterDateIcon} />
          <div className={styles.FiltersText}>{getFiltersText()}</div>
          <Icon
            className={styles.DeleteIcon}
            icon={Close}
            size={1}
            onClick={(e) => {
              e.stopPropagation();
              onFiltersChange({
                ...filters,
                startDay: undefined,
                endDay: undefined,
              });
            }}
          />
        </li>
        {showTimeRange ? (
          <li
            className={classnames({
              [styles.FilterStart]: true,
              [styles.Applied]: !!startTime || !!endTime,
            })}
            onClick={() => setIsModalOpen(true)}
          >
            <ClockSvg className={styles.FilterIcon} />
            {startTime
              ? `${moment(startTime).format(TIME_FILTER_PILL_FORMAT)}${
                  endTime
                    ? ` - ${moment(endTime).format(TIME_FILTER_PILL_FORMAT)}`
                    : ''
                }`
              : t('Time')}

            <Icon
              className={styles.DeleteIcon}
              icon={Close}
              size={1}
              onClick={(e) => {
                e.stopPropagation();
                onFiltersChange({
                  ...filters,
                  startTime: undefined,
                  endTime: undefined,
                });
              }}
            />
          </li>
        ) : null}
      </ul>
      {displayBy && (
        <ul className={styles.Filters}>
          <li className={classnames(styles.FilterEnd, styles.Applied)}>
            <span className={styles.SelectValue}>
              {t('Sort') + ': ' + t(sortingMap[sort])}
            </span>
            <select
              className={styles.Select}
              value={sort}
              onChange={(e) => {
                onSortChange(e.target.value as api.EhrTimeSlot.Sorting);
              }}
            >
              {Object.values(displayBySortMap[displayBy]).map((sort) => (
                <option value={sort} key={sort}>
                  {t(sortingMap[sort])}
                </option>
              ))}
            </select>
            <Icon className={styles.FilterIcon} icon={ChevronDown} size={1} />
          </li>
        </ul>
      )}
    </div>
  );
};

export default Filters;
